/**
 * Fit finder size logic (no DOM). Shared by the fit-finder panel and its
 * Node check (assets are plain scripts, so it exports via globalThis).
 *
 * Body chest girth is estimated from BMI and height, the usual
 * anthropometric shortcut, then nudged by the shopper's shape answers and
 * fit preference. The chart says, per size, the largest body chest it fits
 * and the tallest height its length suits. Charts come from theme settings
 * so the brand can correct them; defaults follow common international tee
 * charts.
 */
(function (root) {
  const SHAPE = {
    chest: { narrow: -3, average: 0, broad: 4 },
    stomach: { flat: -1, average: 0, round: 3 },
    hips: { narrow: -1, average: 0, broad: 2 },
  };
  const FIT = { slim: -3, regular: 0, relaxed: 5 };

  /** "S:96:176, M:104:182" -> [{size:'S', chest:96, height:176}, ...] */
  function parseChart(text) {
    return String(text || '')
      .split(',')
      .map((part) => part.trim().split(':').map((x) => x.trim()))
      .filter((p) => p.length >= 2 && p[0] && !isNaN(+p[1]))
      .map(([size, chest, height]) => ({ size, chest: +chest, height: height ? +height : Infinity }));
  }

  function estimateChest({ gender, heightCm, weightKg }) {
    const bmi = weightKg / Math.pow(heightCm / 100, 2);
    return gender === 'female'
      ? 88 + 2.4 * (bmi - 22) + 0.12 * (heightCm - 165)
      : 100 + 2.6 * (bmi - 24) + 0.12 * (heightCm - 178);
  }

  /**
   * @param profile {gender, heightCm, weightKg, chest, stomach, hips, fit}
   * @param chart   parsed chart, smallest size first
   * @param offered sizes this product is made in (order irrelevant); optional
   * @returns {size, chest, index} - size is always one of `offered` when given
   */
  function recommend(profile, chart, offered) {
    let chest = estimateChest(profile);
    chest += SHAPE.chest[profile.chest] || 0;
    chest += SHAPE.stomach[profile.stomach] || 0;
    chest += SHAPE.hips[profile.hips] || 0;
    const target = chest + (FIT[profile.fit] || 0);

    let i = chart.findIndex((row) => target <= row.chest);
    if (i === -1) i = chart.length - 1;
    // Length: a tee may sit one size shorter than ideal for the height, not
    // more. Slim is asked-for close fit, so length doesn't pull it up.
    if (profile.fit !== 'slim') {
      let h = chart.findIndex((row) => profile.heightCm - 3 <= row.height);
      if (h === -1) h = chart.length - 1;
      i = Math.max(i, h - 1);
    }

    if (offered && offered.length) {
      const have = chart.map((row, idx) => (offered.includes(row.size) ? idx : -1)).filter((idx) => idx >= 0);
      if (have.length && !have.includes(i)) {
        // nearest made size, preferring the larger one on a tie (a tee too
        // small is worse than one a little loose)
        i = have.reduce((best, idx) => {
          const d = Math.abs(idx - i), bd = Math.abs(best - i);
          return d < bd || (d === bd && idx > best) ? idx : best;
        });
      }
    }
    return { size: chart[i].size, chest: Math.round(chest), index: i };
  }

  const cmToFtIn = (cm) => { const t = Math.round(cm / 2.54); return [Math.floor(t / 12), t % 12]; };
  const kgToLb = (kg) => Math.round(kg * 2.20462);

  root.FitFinderCore = { parseChart, estimateChest, recommend, cmToFtIn, kgToLb };
  if (typeof module !== 'undefined') module.exports = root.FitFinderCore;
})(typeof globalThis !== 'undefined' ? globalThis : window);
